import React, { useState } from 'react';
import { Layout, Fieldset, Form, Button, Card } from '@vwfs-bronson/bronson-react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import { CustomerRegistrationError, CustomerRegistrationData, DATE_FORMAT } from 'cpie-common';
import { WithDefaultCpIntegrationErrors, formatCpDate } from '@cp-shared-15/common-utilities';
import {
    ValidatedInput,
    ValidatedCheckbox,
    preventSubmit,
    CleaveInput,
    ScrollToTopOnNavigation,
    Notification,
    NotificationStatus,
    useAnalyticsFormTracker,
    useAnalyticsActionTracker,
} from '@cp-shared-15/frontend-ui';
import { identificationFormValidationSchema } from './validation';

type IdentificationFormProps = {
    legalNotice: string;
    onSubmit: (data: CustomerRegistrationData) => void;
    errorCode?: WithDefaultCpIntegrationErrors<CustomerRegistrationError>;
    setLastTouchedField: (name: string) => void;
};

type FormProps = {
    name: string;
    surname: string;
    birthDate: string;
    confirmPrivacyPolicy: boolean;
    confirmLegalNotice: boolean;
};

export const IdentificationForm: React.FC<IdentificationFormProps> = ({
    legalNotice,
    onSubmit,
    errorCode,
    setLastTouchedField,
}) => {
    const { t } = useTranslation('registration');
    const [showLegalNotice, setShowLegalNotice] = useState(false);

    const { onTyping, onSubmit: trackOnSubmit } = useAnalyticsFormTracker({
        startTyping: 'onStartTypingConfirmIdentity',
        confirm: 'onConfirmConfirmIdentiy',
    });
    const { onAction } = useAnalyticsActionTracker('onFormValidationErrorConfirmIdentity');

    const getErrorNotificationLabel = (): string | React.ReactNode => {
        let label;
        switch (errorCode) {
            case 'CUSTOMER_NOT_FOUND':
                label = t('error.user-not-found');
                break;
            case 'PHONE_NUMBER_NOT_FOUND':
                label = (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t('error.phone-number-not-found'),
                        }}
                    />
                );
                break;
            case 'ACCOUNT_ID_NOT_FOUND':
                label = t('error.account-not-found');
                break;
            case 'CUSTOMER_ALREADY_REGISTERED':
                label = t('error.user-already-registered');
                break;
            case 'EXCEEDED_LIMIT':
                label = (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t('error.exceeded-limit'),
                        }}
                    />
                );
                break;
            case 'THROTTLER_EXCEPTION':
                label = (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t('error.too-many-requests'),
                        }}
                    />
                );
                break;
            default:
                label = t('error.default');
                break;
        }
        return label;
    };

    const handleSubmit = (values: FormProps): void => {
        const { name, surname, birthDate } = values;
        const registrationData: CustomerRegistrationData = {
            firstName: name.trim(),
            lastName: surname.trim(),
            dateOfBirth: formatCpDate(birthDate, DATE_FORMAT).toCpDate(),
        };
        trackOnSubmit();
        onSubmit(registrationData);
    };

    const getErrors = (errors: { [k: string]: string | undefined }): string => Object.keys(errors).join(`, `);
    const initialListErrors = 'name, surname, birthDate, confirmPrivacyPolicy, confirmLegalNotice';

    return (
        <>
            <Layout.Item default={'1/1'} className={'u-mb u-text-center'}>
                <h1>{t('head.title')}</h1>
                <h2>{t('head.info-identification')}</h2>
                <p
                    dangerouslySetInnerHTML={{
                        __html: t('head.info-legal-identification', {
                            privacyPolicyPath: 'https://www.vwfs.ie/privacy_policy.html',
                        }),
                    }}
                />
                {errorCode && (
                    <ScrollToTopOnNavigation>
                        <Notification testId={'errorNotificationIdentificationForm'} status={NotificationStatus.error}>
                            {getErrorNotificationLabel()}
                        </Notification>
                    </ScrollToTopOnNavigation>
                )}
            </Layout.Item>
            <Layout.Item>
                <Formik
                    initialValues={{
                        name: '',
                        surname: '',
                        birthDate: '',
                        confirmPrivacyPolicy: false,
                        confirmLegalNotice: false,
                    }}
                    validationSchema={identificationFormValidationSchema(t)}
                    onSubmit={handleSubmit}
                >
                    {(formik): JSX.Element => (
                        <Form
                            onSubmit={preventSubmit}
                            onChange={(): void => onTyping(formik.errors, formik.touched)}
                            noValidate={true}
                        >
                            <Fieldset>
                                <Fieldset.Row>
                                    <ValidatedInput
                                        required
                                        inputId="name"
                                        autocomplete="given-name"
                                        label={t('form.input-labels.name')}
                                        name="name"
                                        onFocus={(): void => setLastTouchedField('name')}
                                        tooltip={t('form.input-tooltips.name')}
                                    />
                                </Fieldset.Row>
                                <Fieldset.Row>
                                    <ValidatedInput
                                        required
                                        inputId="surname"
                                        autocomplete="family-name"
                                        label={t('form.input-labels.surname')}
                                        name="surname"
                                        onFocus={(): void => setLastTouchedField('surname')}
                                        tooltip={t('form.input-tooltips.surname')}
                                    />
                                </Fieldset.Row>
                                <Fieldset.Row>
                                    <CleaveInput
                                        cleaveOptions={{
                                            delimiter: '/',
                                            blocks: [2, 2, 4],
                                            numericOnly: true,
                                        }}
                                        required
                                        inputId="birthDate"
                                        autocomplete="bday"
                                        inputMode={'numeric'}
                                        label={t('form.input-labels.birth-date')}
                                        name={'birthDate'}
                                        testId={'birthDate'}
                                        onFocus={(): void => setLastTouchedField('birthDate')}
                                        tooltip={t('form.input-tooltips.birth-date')}
                                    />
                                </Fieldset.Row>
                                <Fieldset.Row>
                                    <ValidatedCheckbox
                                        isMandatory
                                        label={
                                            <>
                                                {t('form.input-labels.accept')}{' '}
                                                <a
                                                    href="https://www.vwfs.ie/privacy_policy.html"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid="showPrivacyPolicy"
                                                    className="u-text-uppercase"
                                                >
                                                    <strong>{t('form.button-labels.privacy-policy')}</strong>
                                                </a>
                                            </>
                                        }
                                        id="confirmPrivacyPolicy"
                                        name="confirmPrivacyPolicy"
                                        onFocus={(): void => setLastTouchedField('confirmPrivacyPolicy')}
                                    />
                                </Fieldset.Row>
                                <Fieldset.Row>
                                    <ValidatedCheckbox
                                        isMandatory
                                        id="showLegalNotice"
                                        label={
                                            <>
                                                {t('form.input-labels.accept')}{' '}
                                                <a
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        setShowLegalNotice(!showLegalNotice);
                                                    }}
                                                    data-testid="showLegalNotice"
                                                    className="u-text-uppercase"
                                                >
                                                    <strong>{t('form.button-labels.legal-notice')}</strong>
                                                </a>
                                            </>
                                        }
                                        name="confirmLegalNotice"
                                        onFocus={(): void => setLastTouchedField('confirmLegalNotice')}
                                    />
                                </Fieldset.Row>
                                {showLegalNotice && (
                                    <Fieldset.Row testId="legalNotice">
                                        <Card title="" scrollable={true}>
                                            <div
                                                className={'u-text-left'}
                                                dangerouslySetInnerHTML={{ __html: legalNotice }}
                                            />
                                        </Card>
                                    </Fieldset.Row>
                                )}
                                <Fieldset.Row>
                                    <Button
                                        full={true}
                                        onClick={async (): Promise<void> => {
                                            await formik.submitForm();
                                            if (!isEmpty(formik.errors) || isEmpty(formik.touched)) {
                                                const errorToString = getErrors(formik.errors).toString();
                                                if (!errorToString) {
                                                    onAction(initialListErrors);
                                                } else onAction(getErrors(formik.errors));
                                            }
                                        }}
                                        testId={'submitRegistrationButton'}
                                    >
                                        {t('form.button-labels.submit')}
                                    </Button>
                                </Fieldset.Row>
                            </Fieldset>
                        </Form>
                    )}
                </Formik>
            </Layout.Item>
        </>
    );
};
